import React, { useState, useEffect } from 'react';
import api from '../config/axios';
import toast from 'react-hot-toast';
import {
  ChatBubbleLeftRightIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  EyeIcon,
  XMarkIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const AdminSessions = () => {
  const [bots, setBots] = useState([]);
  const [filteredBots, setFilteredBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalSessions, setTotalSessions] = useState(0);
  const [selectedBot, setSelectedBot] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [showSessionsModal, setShowSessionsModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchBots();
  }, []);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredBots(bots);
    } else {
      const term = searchTerm.toLowerCase();
      setFilteredBots(bots.filter(b => b.name.toLowerCase().includes(term)));
    }
  }, [searchTerm, bots]);

  const fetchBots = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/sessions');
      setBots(response.data.bots);
      setTotalSessions(response.data.totalSessions);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      toast.error('Failed to load session data');
    } finally {
      setLoading(false);
    }
  };

  const viewSessions = async (bot) => {
    setSelectedBot(bot);
    setShowSessionsModal(true);
    setSessionsLoading(true);
    try {
      const response = await api.get(`/admin/sessions/${bot.id}`);
      setSessions(response.data.sessions);
    } catch (error) {
      toast.error('Failed to load sessions');
      setSessions([]);
    } finally {
      setSessionsLoading(false);
    }
  };

  const deleteAllSessions = async (bot) => {
    setDeleting(true);
    try {
      const response = await api.delete(`/admin/sessions/${bot.id}`);
      toast.success(`Deleted ${response.data.deleted} session(s) for ${bot.name}`);
      setShowDeleteConfirm(null);
      setShowSessionsModal(false);
      fetchBots();
    } catch (error) {
      toast.error('Failed to delete sessions');
    } finally {
      setDeleting(false);
    }
  };

  const deleteSession = async (sessionId) => {
    if (!selectedBot) return;
    try {
      await api.delete(`/admin/sessions/${selectedBot.id}/${sessionId}`);
      toast.success('Session deleted');
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      // Update count in the bots list
      setBots(prev => prev.map(b =>
        b.id === selectedBot.id ? { ...b, sessionCount: b.sessionCount - 1 } : b
      ));
      setTotalSessions(prev => prev - 1);
    } catch (error) {
      toast.error('Failed to delete session');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const d = new Date(dateStr);
    return d.toLocaleString('en-ZA', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const timeSince = (dateStr) => {
    if (!dateStr) return '';
    const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-neutral-800">Session Management</h1>
          <p className="mt-1 text-sm text-neutral-500">
            View and clear stuck chat sessions for your bots
          </p>
        </div>
        <button
          onClick={fetchBots}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-neutral-600 bg-white border border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors"
        >
          <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-neutral-200 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-primary-50 rounded-lg">
              <ChatBubbleLeftRightIcon className="h-5 w-5 text-primary-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-neutral-800">{totalSessions}</p>
              <p className="text-xs text-neutral-500">Active Sessions</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-neutral-200 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-50 rounded-lg">
              <ChatBubbleLeftRightIcon className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-neutral-800">{bots.length}</p>
              <p className="text-xs text-neutral-500">Total Bots</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-neutral-200 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-amber-50 rounded-lg">
              <ExclamationTriangleIcon className="h-5 w-5 text-amber-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-neutral-800">
                {bots.filter(b => b.sessionCount > 0).length}
              </p>
              <p className="text-xs text-neutral-500">Bots with Sessions</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl border border-neutral-200 p-4">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-400" />
          <input
            type="text"
            placeholder="Search bots by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 text-sm border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          />
        </div>
      </div>

      {/* Bot list */}
      <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <ArrowPathIcon className="h-6 w-6 text-neutral-400 animate-spin" />
            <span className="ml-2 text-neutral-500">Loading bots...</span>
          </div>
        ) : filteredBots.length === 0 ? (
          <div className="text-center py-12">
            <ChatBubbleLeftRightIcon className="mx-auto h-10 w-10 text-neutral-300" />
            <p className="mt-2 text-sm text-neutral-500">No bots found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider">Bot</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-neutral-500 uppercase tracking-wider">Sessions</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-neutral-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200">
                {filteredBots.map((bot) => (
                  <tr key={bot.id} className="hover:bg-neutral-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white text-sm">
                          {bot.icon || bot.name.charAt(0)}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-neutral-800">{bot.name}</p>
                          <p className="text-xs text-neutral-400 font-mono">{bot.id.slice(0, 16)}...</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {bot.publishedTypebotId ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          Published
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-neutral-100 text-neutral-600">
                          Draft
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {bot.sessionCount > 0 ? (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-700">
                          {bot.sessionCount}
                        </span>
                      ) : (
                        <span className="text-xs text-neutral-400">0</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {bot.sessionCount > 0 && (
                          <>
                            <button
                              onClick={() => viewSessions(bot)}
                              className="p-2 text-neutral-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="View sessions"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => setShowDeleteConfirm(bot)}
                              className="p-2 text-neutral-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete all sessions"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Sessions Detail Modal */}
      {showSessionsModal && selectedBot && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-neutral-900/60 backdrop-blur-sm" onClick={() => setShowSessionsModal(false)} />
            <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
              {/* Modal header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
                <div>
                  <h3 className="text-lg font-semibold text-neutral-800">
                    Sessions: {selectedBot.name}
                  </h3>
                  <p className="text-sm text-neutral-500">{sessions.length} active session(s)</p>
                </div>
                <div className="flex items-center gap-2">
                  {sessions.length > 0 && (
                    <button
                      onClick={() => setShowDeleteConfirm(selectedBot)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                    >
                      <TrashIcon className="h-3.5 w-3.5" />
                      Delete All
                    </button>
                  )}
                  <button
                    onClick={() => setShowSessionsModal(false)}
                    className="p-2 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-lg transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Modal body */}
              <div className="flex-1 overflow-y-auto px-6 py-4">
                {sessionsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <ArrowPathIcon className="h-5 w-5 text-neutral-400 animate-spin" />
                    <span className="ml-2 text-sm text-neutral-500">Loading sessions...</span>
                  </div>
                ) : sessions.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-sm text-neutral-500">No active sessions</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {sessions.map((session) => (
                      <div
                        key={session.id}
                        className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg border border-neutral-200 hover:border-neutral-300 transition-colors"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-mono text-neutral-700 truncate">{session.id}</p>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-xs text-neutral-400">
                              Created: {formatDate(session.createdAt)}
                            </span>
                            <span className="text-xs text-neutral-400">
                              Updated: {timeSince(session.updatedAt)}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => deleteSession(session.id)}
                          className="ml-3 p-1.5 text-neutral-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                          title="Delete this session"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-neutral-900/60 backdrop-blur-sm" onClick={() => setShowDeleteConfirm(null)} />
            <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-red-100 rounded-xl">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-neutral-800">
                    Delete All Sessions
                  </h3>
                  <p className="mt-2 text-sm text-neutral-600">
                    This will delete <strong>{showDeleteConfirm.sessionCount} session(s)</strong> for{' '}
                    <strong>{showDeleteConfirm.name}</strong>.
                  </p>
                  <p className="mt-1 text-sm text-neutral-500">
                    All users on this bot (including WhatsApp) will restart their conversation from the beginning.
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  disabled={deleting}
                  className="px-4 py-2 text-sm font-medium text-neutral-600 bg-white border border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteAllSessions(showDeleteConfirm)}
                  disabled={deleting}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  {deleting ? (
                    <>
                      <ArrowPathIcon className="h-4 w-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <TrashIcon className="h-4 w-4" />
                      Delete All Sessions
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSessions;
